"use client";

import React from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { AnimatePresence, motion } from "motion/react";
import { useRouter } from "next/navigation";

import { cn } from "../../../../utils/cn";


interface Props {
    show?: boolean;
    onClose?: () => void;
    onCloseHref?: string;
    contain?: boolean;
    disablePadding?: boolean;
    disableAnimation?: boolean;
    className?: string;
    children: (({ onClose }: { onClose: () => void }) => React.ReactNode) | React.ReactNode;
}

const Modal = ({
    show,
    onClose = () => {},
    onCloseHref, // Used for server side Modals as onClick is a client side action.
    contain,
    disablePadding,
    disableAnimation,
    className,
    children,
}: Props) => {
    const router = useRouter();

    const dialogPanelClassName = cn(
        'relative max-h-[95dvh] bg-page-foreground rounded-200 overflow-auto',
        !disablePadding ? 'p-200' : '',
        contain === true ? 'max-w-2xl' : '',
        className
    );

    const handleClose = () => {
        if (onCloseHref) {
            router.push(onCloseHref)
            return;
        }

        onClose();
    }

    return (
        <AnimatePresence initial={!disableAnimation}>
            {show ? (
                <Dialog
                    static
                    open={show}
                    data-testid="modal-dialog"
                    as="div"
                    className={`fixed inset-0 flex items-center justify-center w-full h-full p-050 backdrop-blur-sm z-100 sm:p-100 md:p-025`}
                    onClose={handleClose}
                >
                    <DialogBackdrop
                        as={motion.div}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-page-overlay pointer-events-none"
                    />
                    <DialogPanel
                        as={motion.div}
                        initial={{ opacity: 0, scale: 0.7 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.7 }}
                        className={dialogPanelClassName}
                    >
                        {typeof children === 'function' ? children({ onClose: handleClose }) : children}
                    </DialogPanel>
                </Dialog>
            ) : null}
        </AnimatePresence>
    );
};

export default Modal;
